// Lucka 4: Bevakning av kompetensbevis, besiktningar och tillstånd som håller på att gå ut.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import DocumentHeader from "./DocumentHeader";
import { BellRing, AlertTriangle, Clock, CheckCircle2 } from "lucide-react";

interface Props {
  projectId: string;
}

interface WatchItem {
  id: string;
  category: string;
  label: string;
  detail: string;
  date: string;
  days: number;
}

const WARN_DAYS = 30;

const daysUntil = (d: string) => Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);

const ExpiryWatch: React.FC<Props> = ({ projectId }) => {
  const [items, setItems] = useState<WatchItem[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [inspections, competences, hotWork, traffic, inductions] = await Promise.all([
      supabase
        .from("equipment_inspections")
        .select("id, object_name, equipment_type, next_inspection, status")
        .eq("project_id", projectId)
        .not("next_inspection", "is", null),
      supabase
        .from("work_preparation_competences")
        .select("id, requirement_label, valid_to, verified")
        .eq("project_id", projectId)
        .not("valid_to", "is", null),
      supabase
        .from("hot_work_permits")
        .select("id, location, valid_to, status")
        .eq("project_id", projectId)
        .not("valid_to", "is", null),
      supabase
        .from("traffic_plans")
        .select("id, title, valid_to, status")
        .eq("project_id", projectId)
        .not("valid_to", "is", null),
      supabase
        .from("safety_inductions")
        .select("id, person_name, company, valid_to")
        .eq("project_id", projectId)
        .not("valid_to", "is", null),
    ]);

    if (inspections.error || competences.error) toast.error("Kunde inte läsa alla bevakningar");

    const list: WatchItem[] = [];

    (inspections.data ?? []).forEach((r) => {
      if (!r.next_inspection || r.status === "retired") return;
      list.push({
        id: `insp-${r.id}`,
        category: "Besiktning",
        label: r.object_name,
        detail: r.equipment_type,
        date: r.next_inspection,
        days: daysUntil(r.next_inspection),
      });
    });

    (competences.data ?? []).forEach((r) => {
      if (!r.valid_to) return;
      list.push({
        id: `comp-${r.id}`,
        category: "Kompetensbevis",
        label: r.requirement_label,
        detail: r.verified ? "Verifierat" : "Ej verifierat",
        date: r.valid_to,
        days: daysUntil(r.valid_to),
      });
    });

    (hotWork.data ?? []).forEach((r) => {
      if (!r.valid_to || r.status === "closed") return;
      list.push({
        id: `hot-${r.id}`,
        category: "Heta arbeten",
        label: r.location,
        detail: "Tillståndets giltighetstid",
        date: r.valid_to,
        days: daysUntil(r.valid_to),
      });
    });

    (traffic.data ?? []).forEach((r) => {
      if (!r.valid_to) return;
      list.push({
        id: `ta-${r.id}`,
        category: "TA-plan",
        label: r.title,
        detail: r.status === "approved" ? "Godkänd" : "Utkast",
        date: r.valid_to,
        days: daysUntil(r.valid_to),
      });
    });

    ((inductions.data as { id: string; person_name: string; company: string | null; valid_to: string | null }[] | null) ?? []).forEach((r) => {
      if (!r.valid_to) return;
      list.push({
        id: `intro-${r.id}`,
        category: "Säkerhetsintroduktion",
        label: r.person_name,
        detail: r.company ?? "Säkerhetsintroduktion",
        date: r.valid_to,
        days: daysUntil(r.valid_to),
      });
    });

    list.sort((a, b) => a.days - b.days);
    setItems(list);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const expired = useMemo(() => items.filter((i) => i.days < 0), [items]);
  const soon = useMemo(() => items.filter((i) => i.days >= 0 && i.days <= WARN_DAYS), [items]);
  const ok = useMemo(() => items.filter((i) => i.days > WARN_DAYS), [items]);

  const Row = ({ item }: { item: WatchItem }) => (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{item.label}</span>
          <Badge variant="outline">{item.category}</Badge>
        </div>
        <p className="text-xs text-muted-foreground">{item.detail}</p>
      </div>
      <div className="text-right">
        <div className="text-sm">{new Date(item.date).toLocaleDateString("sv-SE")}</div>
        <div className="text-xs text-muted-foreground">
          {item.days < 0
            ? `${Math.abs(item.days)} dagar försenat`
            : item.days === 0
              ? "går ut i dag"
              : `om ${item.days} dagar`}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <DocumentHeader
        docNumber="06d"
        title="Bevakning – datum som går ut"
        description="Kompetensbevis, återkommande besiktningar, heta arbeten och TA-planer samlade efter förfallodatum."
        icon={BellRing}
      />

      {loading ? (
        <p className="text-sm text-muted-foreground">Laddar…</p>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Inget att bevaka ännu. När besiktningar, kompetensbevis eller tillstånd får ett slutdatum dyker de upp här.
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className="h-4 w-4 text-destructive" /> Har gått ut ({expired.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {expired.length === 0 ? (
                <p className="text-sm text-muted-foreground">Inget har passerat sitt datum.</p>
              ) : (
                expired.map((i) => <Row key={i.id} item={i} />)
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="h-4 w-4" /> Går ut inom {WARN_DAYS} dagar ({soon.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {soon.length === 0 ? (
                <p className="text-sm text-muted-foreground">Inget på väg att gå ut.</p>
              ) : (
                soon.map((i) => <Row key={i.id} item={i} />)
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <CheckCircle2 className="h-4 w-4 text-primary" /> Giltiga ({ok.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {ok.map((i) => (
                <Row key={i.id} item={i} />
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ExpiryWatch;
